import { Link } from "react-router-dom";
import DatafieldSection from "../../../components/datafield-section";
import { DrawdownLoan, RetailODDetails, TermLoanDetails } from "../../../dto";
import {
  FieldLabel,
  RetailODDrawdownStatusEnum,
  RoutePaths,
} from "../../../models";
import { dateToDDMMYYYY, splitKeyWithSpace } from "../../../utils";
import { ExtendedLoanType } from "../types";

interface LoanDetailsHeaderProps {
  loanDetails?: TermLoanDetails | RetailODDetails | null;
  drawdownLoanDetails?: DrawdownLoan | null;
  isDrawdownLoan?: boolean;
}

function LoanApplicationIdLink(loanApplicationId: string) {
  return (
    <Link to={`${RoutePaths.APPLICATIONS}/${loanApplicationId}`}>
      {loanApplicationId}
    </Link>
  );
}

const LOAN_TYPE_TO_LEFT_LABEL_MAP: Record<ExtendedLoanType, FieldLabel[]> = {
  TERM_LOAN: [
    { label: "Loan ID", key: "_id" },
    {
      label: "Loan Application",
      key: "appId",
      formatMethod: LoanApplicationIdLink,
    },
  ],
  RETAIL_OD: [
    {
      label: "Loan ID",
      key: "_id",
    },
    {
      label: "Loan Application",
      key: "appId",
      formatMethod: LoanApplicationIdLink,
    },
  ],
  RETAIL_OD_DRAWDOWN: [
    { label: "Drawdown ID", key: "_id", formatMethod: splitKeyWithSpace },
    {
      label: "Date",
      key: "statusLogs",
      formatMethod: (
        statusLogs: Array<{
          status: RetailODDrawdownStatusEnum;
          updatedAt: string;
        }>
      ) =>
        dateToDDMMYYYY(
          statusLogs.find(
            ({ status }) => status === RetailODDrawdownStatusEnum.INITIATED
          )?.updatedAt
        ),
    },
  ],
};

const LOAN_TYPE_TO_RIGHT_LABEL_MAP: Record<ExtendedLoanType, FieldLabel[]> = {
  TERM_LOAN: [
    { label: "Sanctioned On", key: "createdAt", formatMethod: dateToDDMMYYYY },
    { label: "Status", key: "status" },
  ],
  RETAIL_OD: [
    { label: "Sanctioned On", key: "createdAt", formatMethod: dateToDDMMYYYY },
    { label: "Status", key: "status" },
  ],
  RETAIL_OD_DRAWDOWN: [{ label: "Status", key: "status" }],
};

export default function LoanDetailsHeader({
  loanDetails,
  drawdownLoanDetails,
  isDrawdownLoan,
}: LoanDetailsHeaderProps) {
  let loanType: ExtendedLoanType;
  let data: TermLoanDetails | RetailODDetails | DrawdownLoan | null;

  if (isDrawdownLoan) {
    if (!drawdownLoanDetails) return null;
    loanType = "RETAIL_OD_DRAWDOWN";
    data = drawdownLoanDetails;
  } else {
    if (
      !loanDetails ||
      (loanDetails.loanType !== "TERM_LOAN" &&
        loanDetails.loanType !== "RETAIL_OD")
    ) {
      return null;
    }
    loanType = loanDetails.loanType as ExtendedLoanType;
    data = loanDetails;
  }

  const labelsLeft = LOAN_TYPE_TO_LEFT_LABEL_MAP[loanType];
  const labelsRight = LOAN_TYPE_TO_RIGHT_LABEL_MAP[loanType];

  if (!labelsLeft || !labelsRight) return null;

  return (
    <DatafieldSection
      labels={[
        {
          items: labelsLeft,
        },
        {
          items: labelsRight,
        },
      ]}
      data={data}
    />
  );
}
